import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { baseService } from './base.service'
import { CreateCourseDto, UpdateCourseDto } from './dto/course-dto.interface'
import { IBaseService } from './interfaces/base-service.interface'

import { QueryParams } from '@/types/base.type'
import { CourseUserType } from '@/types/course.type'
import type { Course as CourseType } from '@/types/course-detail.type'
import { ListResponse } from '@/types/list.type'

export class CourseService {
  private static instance: CourseService
  private readonly api: AxiosInstance

  constructor(private readonly baseService: IBaseService) {
    this.api = this.baseService.getClient()
  }

  private getQueryParams(locale?: string) {
    return locale ? `?locale=${locale}` : ''
  }

  public static getInstance(baseService: IBaseService): CourseService {
    if (!CourseService.instance) {
      CourseService.instance = new CourseService(baseService)
    }
    return CourseService.instance
  }

  getListAllCourses = async <T>(url = '/course/list-all', configs?: AxiosRequestConfig) => {
    const { data } = await this.api.get<T>(url, configs)
    return data
  }

  getCourses = async ({ queryParams, configs }: { queryParams: QueryParams; configs?: AxiosRequestConfig }) => {
    const { pageNumber, pageSize, sort, search, searchFields } = queryParams
    let url = '/course/course-admin'

    if (pageNumber && pageSize) {
      url += `?pageNumber=${pageNumber}&pageSize=${pageSize}`
    }
    if (sort) {
      url += `&sort=${sort}`
    }
    if (search) {
      url += `&search=${encodeURIComponent(search)}`
    }
    if (searchFields) {
      url += `&searchFields=${searchFields.join(',')}`
    }

    const { data } = await this.api.get<ListResponse<CourseType>>(url, configs)
    return data
  }

  getMyCurrentCourses = async (type?: 'instructor' | 'student', configs?: AxiosRequestConfig) => {
    let url = '/course/my-learning/list'
    if (type) {
      url += `?type=${type}`
    }
    const { data } = await this.api.get<Array<CourseType>>(url, configs)
    return data
  }

  createCourse = async (courseDto: CreateCourseDto, locale?: string) => {
    const { data } = await this.api.post(`/course/create${this.getQueryParams(locale)}`, courseDto)
    return data
  }

  getAdminCourseById = async <T>(id: number, options?: AxiosRequestConfig, locale?: string) => {
    const { data } = await this.api.get<T>(`/course/${id}${this.getQueryParams(locale)}`, options)
    return data
  }

  updateCourse = async (id: number, courseDto: UpdateCourseDto, locale?: string) => {
    const { data } = await this.api.put(`/course/update/${id}${this.getQueryParams(locale)}`, courseDto)
    return data
  }

  deleteCourseVideo = async (courseId: number, videoId: number) => {
    const { data } = await this.api.delete(`/course/${courseId}/video/${videoId}`)
    return data
  }

  getCourseBySlug = async <T = CourseType>(slug: string, options?: AxiosRequestConfig, locale?: string) => {
    const { data } = await this.api.get<T>(`/course/slug/${slug}${this.getQueryParams(locale)}`, options)
    return data
  }

  deleteCourse = async (id: number, options?: AxiosRequestConfig) => {
    const { data } = await this.api.delete(`/course/${id}`, options)
    return data
  }

  // course users - instructor dashboard
  getCourseUsers = async ({
    queryParams,
    courseId,
    configs,
  }: {
    queryParams: QueryParams
    courseId: number
    configs?: AxiosRequestConfig
  }) => {
    const { pageNumber, pageSize, sort, search, searchFields } = queryParams
    const params: Record<string, string | number> = {}
    if (pageNumber != null) params.pageNumber = pageNumber
    if (pageSize != null) params.pageSize = pageSize
    if (sort) params.sort = sort
    if (search) params.search = search
    if (searchFields?.length) params.searchFields = searchFields.join(',')

    const { data } = await this.api.get<ListResponse<CourseUserType>>(`/course-users/course/${courseId}/students`, {
      ...configs,
      params,
    })
    return data
  }

  getCourseUserDetail = async (
    { courseId, studentId }: { courseId: number; studentId: number },
    configs?: AxiosRequestConfig
  ) => {
    const { data } = await this.api.get<CourseUserType>(
      `/course-users/course/${courseId}/students/${studentId}`,
      configs
    )
    return data
  }

  editUserProjectScore = async (userId: number, projectId: number, score: number) => {
    const { data } = await this.api.put(`/course-users/user/${userId}/project-submission/${projectId}`, { score })
    return data
  }

  editCourseUserCertification = async (courseId: number, userId: number, { isCertified }: { isCertified: boolean }) => {
    const { data } = await this.api.put(`/course-users/course/${courseId}/user/${userId}/certified`, {
      isCertified,
    })
    return data
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getCourseCertification = async <T = any>(certificateCode: string, configs?: AxiosRequestConfig) => {
    const { data } = await this.api.get<T>(`/course-users/certificate/${certificateCode}`, configs)
    return data
  }

  getCourseProjectSubmissions = async <T>(courseId: number, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<T>(`/course/${courseId}/project-submissions`, options)
    return data
  }

  getCourseQuizzes = async <T>(courseId: number, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<T>(`/course/${courseId}/quizzes`, options)
    return data
  }

  getCourseTopics = async <T>(
    courseId: number,
    pageNumber = 1,
    pageSize = 5,
    sort = '-updatedAt',
    options?: AxiosRequestConfig
  ) => {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
      sort: sort,
    })
    const { data } = await this.api.get<T>(`/course/${courseId}/topics?${params}`, options)
    return data
  }

  getFreeCourses = async (configs?: AxiosRequestConfig) => {
    const { data } = await this.api.get<Array<CourseType>>('/course/course-free/all', configs)
    return data
  }

  getFreeCourseBySlug = async <T = CourseType>(slug: string, configs?: AxiosRequestConfig) => {
    const { data } = await this.api.get<T>(`/course/course-free/${slug}`, configs)
    return data
  }

  // Get course instructors (for display purposes)
  getCourseInstructors = async <T>(courseId: number, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<T>(`/course/${courseId}/instructors`, options)
    return data
  }
}

const courseService = CourseService.getInstance(baseService)

export default courseService
